/**
 * Drive-Cycle Multiplier — Zenvio Trip Planner
 * =============================================
 * A per-segment energy correction that captures the gap between pure
 * physics-based prediction and real-world driving patterns (stop-and-go,
 * traffic congestion, repeated decelerations).
 *
 * Background
 * ----------
 * The base physics model (drag + roll + grade + HVAC) assumes constant
 * speed within each segment. In reality, a vehicle in urban traffic
 * accelerates and decelerates dozens of times per kilometer, burning extra
 * energy on each acceleration that isn't recovered by regen (because regen
 * is typically 65-75% efficient and is capped at low SoC).
 *
 * The Heliyon paper (Prasad & Gudipalli 2024, doi:10.1016/j.heliyon.2024.e41494)
 * empirically measured the SAME vehicle on four drive cycles and found:
 *
 *   | Drive cycle | Energy (Wh/km) | Multiplier vs WLTP | Avg speed |
 *   | WLTP        | 68.18          | 1.00 (baseline)    | ~46 km/h  |
 *   | NEDC        | 80.75          | 1.18               | ~34 km/h  |
 *   | MIDC        | 86.41          | 1.27               | ~24 km/h  |
 *   | IDC         | 98.51          | 1.45               | ~22 km/h  |
 *
 * Key insight: an Indian user's energy consumption is 27-45% higher than
 * the WLTP-published value, NOT because of physics differences but because
 * of drive-cycle dynamics that the physics model doesn't capture.
 *
 * Strategy
 * --------
 * We use AVERAGE SPEED as the primary proxy for stop-and-go intensity,
 * with optional adjustments for known geographic patterns (an Indian urban
 * segment averaging 22 km/h is genuinely IDC; a German autobahn segment
 * averaging 22 km/h is unusually stuck in traffic but probably temporary).
 *
 * The multiplier is applied to the driving-energy (E_drive) component of
 * each segment AFTER the physics calculation. HVAC energy is not affected
 * since cabin conditioning is independent of drive cycle.
 *
 * Reference
 * ---------
 * [1] Prasad & Gudipalli (2024) "An effective range estimation and state-of-charge
 *     to mitigate range anxiety in electric vehicles", Heliyon Vol 11,
 *     doi:10.1016/j.heliyon.2024.e41494
 *     — Open access CC BY. Validates our 8-equation physics model AND
 *     provides the drive-cycle multipliers used here.
 *
 * Limits
 * ------
 * - The multiplier is bounded to [0.90, 1.60] for physical sanity.
 * - When `avg_speed_kmh` is missing, the multiplier is 1.0 (no correction).
 * - The locale hint is OPTIONAL. Without it, the multiplier is derived
 *   purely from average speed.
 */

// ============ DRIVE-CYCLE SPEED → MULTIPLIER MAPPING ============

/**
 * Speed → multiplier curve, anchored on the Heliyon empirical points.
 *
 *   46 km/h (WLTP) → 1.00
 *   34 km/h (NEDC) → 1.18
 *   24 km/h (MIDC) → 1.27
 *   22 km/h (IDC)  → 1.45
 *
 * Above 46 km/h, the multiplier continues to decrease (steady highway
 * cruising is more efficient than WLTP's mixed-urban). We taper down to
 * 0.92 at 100 km/h to reflect this, then flatten.
 *
 * Below 22 km/h, we extrapolate cautiously: stop-and-go gets worse but the
 * Heliyon paper doesn't cover this region. Cap at 1.55 below 15 km/h.
 */
const SPEED_BREAKPOINTS = [
  // [avg_speed_kmh, multiplier]
  [0,   1.55],   // gridlock — extrapolated cap
  [15,  1.50],
  [22,  1.45],   // IDC anchor
  [24,  1.27],   // MIDC anchor (note IDC is more stop-go than MIDC despite similar avg)
  [34,  1.18],   // NEDC anchor
  [46,  1.00],   // WLTP anchor (baseline)
  [70,  0.96],   // light highway
  [100, 0.92],   // steady cruise
  [130, 0.92],   // flatline above autobahn speeds
];

/**
 * Locale-aware adjustment. Some markets have inherently more stop-go than
 * their average speed alone suggests, due to road quality, traffic
 * patterns, etc.
 *
 * Multipliers here are applied ON TOP of the speed-based curve, NOT
 * instead of it. Default 1.00 (no adjustment).
 *
 * The 1.08 for India captures: poor road surface forcing more
 * acceleration changes than the same average speed would imply in EU/US.
 */
const LOCALE_ADJUSTMENT = {
  'IN': 1.08,    // India — road quality penalty
  'NP': 1.05,    // Nepal — similar but slightly less severe
  'BT': 1.05,    // Bhutan — mountain terrain stop-go
  'BD': 1.06,    // Bangladesh
  'PK': 1.05,    // Pakistan
  'LK': 1.04,    // Sri Lanka
  // Default 1.00 for all other countries
};

const ABSOLUTE_MIN = 0.90;
const ABSOLUTE_MAX = 1.60;

// ============ MAIN FUNCTION ============

/**
 * Compute the drive-cycle multiplier for a single segment.
 *
 * @param {Object} opts
 * @param {number} opts.avg_speed_kmh   Required. Segment average speed.
 * @param {string} [opts.country_code]  Optional ISO 3166-1 alpha-2 ('IN', 'US', ...)
 * @param {string} [opts.road_type]     Optional 'highway' | 'urban' | 'rural'. Currently
 *                                      only used as a hint for confidence reporting.
 *
 * @returns {Object} {multiplier, audit}
 *   multiplier: number ∈ [0.90, 1.60] to apply to E_drive
 *   audit: {speed_factor, locale_factor, applied, capped, source}
 */
export function driveCycleMultiplier(opts) {
  const { avg_speed_kmh, country_code, road_type } = opts || {};

  if (avg_speed_kmh == null || !Number.isFinite(avg_speed_kmh) || avg_speed_kmh <= 0) {
    // No speed → can't infer cycle. Return neutral with audit explaining.
    return {
      multiplier: 1.0,
      audit: {
        speed_factor: 1.0,
        locale_factor: 1.0,
        applied: 1.0,
        capped: false,
        source: 'no-avg-speed-fallback'
      }
    };
  }

  // Speed-based interpolation
  const speed_factor = interpolateBreakpoints(SPEED_BREAKPOINTS, avg_speed_kmh);

  // Locale adjustment (default 1.0)
  const locale_factor = (country_code && LOCALE_ADJUSTMENT[country_code]) || 1.0;

  // Combine and clamp
  let applied = speed_factor * locale_factor;
  let capped = false;
  if (applied < ABSOLUTE_MIN) { applied = ABSOLUTE_MIN; capped = true; }
  if (applied > ABSOLUTE_MAX) { applied = ABSOLUTE_MAX; capped = true; }

  return {
    multiplier: round3(applied),
    audit: {
      speed_factor: round3(speed_factor),
      locale_factor: round3(locale_factor),
      applied: round3(applied),
      capped,
      road_type: road_type ?? null,
      source: 'heliyon-2024-curve'
    }
  };
}

// ============ HELPERS ============

/**
 * Piecewise linear interpolation over a sorted array of [x, y] points.
 * Out-of-range values clamp to the endpoint.
 */
function interpolateBreakpoints(points, x) {
  if (x <= points[0][0]) return points[0][1];
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1];
  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    if (x >= x0 && x <= x1) {
      const t = (x - x0) / (x1 - x0);
      return y0 + (y1 - y0) * t;
    }
  }
  return 1.0;  // unreachable but defensive
}

function round3(x) { return Math.round(x * 1000) / 1000; }
